/**
 * ReportPage - Displays a single category report with Markdown rendering.
 * Route: /report/:reportType
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import billingService from '../services/billingService';
import ProUpgradeBanner from '../components/billing/ProUpgradeBanner';

const REPORT_TYPES = {
  work: { ja: '仕事・キャリア', en: 'Work & Career' },
  romance: { ja: '恋愛・パートナーシップ', en: 'Romance & Partnership' },
  relationships: { ja: '人間関係', en: 'Relationships' },
  stress: { ja: 'ストレス・メンタルケア', en: 'Stress & Mental Care' },
  growth: { ja: '自己成長', en: 'Self Growth' },
  compatibility: { ja: '相性分析', en: 'Compatibility Analysis' },
};

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 8px 16px 48px;
  @media (min-width: 640px) { padding: 24px; }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${(p) => p.theme.colors.textMuted};
  cursor: pointer;
  font-size: 1.2rem;
  padding: 4px;
  &:hover { color: ${(p) => p.theme.colors.text}; }
`;

const TitleGroup = styled.div`
  flex: 1;
`;

const PageTitle = styled.h1`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0;
`;

const PageSub = styled.p`
  font-size: 0.75rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin: 2px 0 0 0;
  font-family: ${(p) => p.theme.fonts.mono};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${(p) => p.theme.colors.border};
  border-top-color: ${(p) => p.theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 80px auto;
`;

const LoadingText = styled.p`
  text-align: center;
  color: ${(p) => p.theme.colors.textMuted};
  font-size: 0.85rem;
  margin-top: 12px;
`;

const ContentBox = styled.div`
  background: ${(p) => p.theme.colors.bgTertiary};
  border-radius: ${(p) => p.theme.borderRadius.lg};
  border: 1px solid ${(p) => p.theme.colors.border};
  padding: 24px 28px;

  @media (max-width: 520px) {
    padding: 20px 16px;
  }
`;

/* ── Markdown Styled Elements ── */

const MarkdownBody = styled.div`
  color: ${(p) => p.theme.colors.text};
  font-size: 0.9rem;
  line-height: 1.8;

  h1, h2 {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${(p) => p.theme.colors.text};
    margin: 28px 0 12px 0;
    padding-bottom: 6px;
    border-bottom: 1px solid ${(p) => p.theme.colors.border};
  }

  h3 {
    font-size: 1rem;
    font-weight: 700;
    color: ${(p) => p.theme.colors.text};
    margin: 22px 0 8px 0;
  }

  h4 {
    font-size: 0.92rem;
    font-weight: 600;
    color: ${(p) => p.theme.colors.text};
    margin: 18px 0 6px 0;
  }

  p {
    margin: 0 0 12px 0;
  }

  ul, ol {
    margin: 0 0 12px 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 6px;
    line-height: 1.7;
  }

  strong {
    color: #a5b4fc;
    font-weight: 600;
  }

  em {
    color: ${(p) => p.theme.colors.textSecondary};
    font-style: italic;
  }

  blockquote {
    border-left: 3px solid ${(p) => p.theme.colors.primary};
    padding-left: 16px;
    margin: 12px 0;
    color: ${(p) => p.theme.colors.textSecondary};
  }

  hr {
    border: none;
    border-top: 1px solid ${(p) => p.theme.colors.border};
    margin: 20px 0;
  }

  code {
    background: rgba(230, 199, 126, 0.10);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.85em;
    font-family: ${(p) => p.theme.fonts.mono};
  }
`;

const ReportSuccessBanner = styled.div`
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: ${(p) => p.theme.borderRadius.md};
  padding: 12px 16px;
  margin-bottom: 24px;
  color: #10B981;
  font-size: 0.88rem;
  font-weight: 600;
`;

const ErrorText = styled.p`
  text-align: center;
  color: ${(p) => p.theme.colors.error};
  padding: 48px 0;
`;

const FooterButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
`;

const FooterLink = styled.button`
  padding: 10px 24px;
  background: ${(p) => p.theme.colors.bgTertiary};
  color: ${(p) => p.theme.colors.text};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.md};
  font-size: 0.85rem;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s;
  &:hover { background: ${(p) => p.theme.colors.bgHover}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RegenerateBtn = styled.button`
  padding: 10px 24px;
  background: rgba(230, 199, 126, 0.10);
  color: ${(p) => p.theme.colors.primary};
  border: 1px solid rgba(230, 199, 126, 0.30);
  border-radius: ${(p) => p.theme.borderRadius.md};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: rgba(230, 199, 126, 0.18);
    border-color: ${(p) => p.theme.colors.primary};
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SmallSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(230, 199, 126, 0.30);
  border-top-color: ${(p) => p.theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

/* ── Markdown Parser ── */

function parseInline(text) {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let keyIdx = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch) {
      const idx = boldMatch.index;
      if (idx > 0) parts.push(remaining.slice(0, idx));
      parts.push(<strong key={keyIdx++}>{boldMatch[1]}</strong>);
      remaining = remaining.slice(idx + boldMatch[0].length);
      continue;
    }
    // Italic: *text*
    const italicMatch = remaining.match(/\*(.+?)\*/);
    if (italicMatch) {
      const idx = italicMatch.index;
      if (idx > 0) parts.push(remaining.slice(0, idx));
      parts.push(<em key={keyIdx++}>{italicMatch[1]}</em>);
      remaining = remaining.slice(idx + italicMatch[0].length);
      continue;
    }
    parts.push(remaining);
    break;
  }
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

function renderMarkdown(content) {
  if (!content) return null;

  const lines = content.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Empty line
    if (!trimmed) { i++; continue; }

    // Headings
    if (trimmed.startsWith('#### ')) {
      elements.push(<h4 key={i}>{parseInline(trimmed.slice(5))}</h4>);
      i++; continue;
    }
    if (trimmed.startsWith('### ')) {
      elements.push(<h3 key={i}>{parseInline(trimmed.slice(4))}</h3>);
      i++; continue;
    }
    if (trimmed.startsWith('## ')) {
      elements.push(<h2 key={i}>{parseInline(trimmed.slice(3))}</h2>);
      i++; continue;
    }
    if (trimmed.startsWith('# ')) {
      elements.push(<h1 key={i}>{parseInline(trimmed.slice(2))}</h1>);
      i++; continue;
    }

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(trimmed)) {
      elements.push(<hr key={i} />);
      i++; continue;
    }

    // Blockquote
    if (trimmed.startsWith('> ')) {
      const quoteLines = [];
      while (i < lines.length && lines[i].trim().startsWith('> ')) {
        quoteLines.push(lines[i].trim().slice(2));
        i++;
      }
      elements.push(<blockquote key={i}>{quoteLines.map((ql, qi) => <p key={qi}>{parseInline(ql)}</p>)}</blockquote>);
      continue;
    }

    // Unordered list
    if (/^[-*] /.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\s*[-*] /.test(lines[i])) {
        items.push(lines[i].trim().replace(/^[-*] /, ''));
        i++;
      }
      elements.push(<ul key={`ul-${i}`}>{items.map((item, idx) => <li key={idx}>{parseInline(item)}</li>)}</ul>);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
        items.push(lines[i].trim().replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(<ol key={`ol-${i}`}>{items.map((item, idx) => <li key={idx}>{parseInline(item)}</li>)}</ol>);
      continue;
    }

    // Paragraph (collect consecutive text lines)
    const paraLines = [];
    while (i < lines.length && lines[i].trim() && !/^#{1,4}\s/.test(lines[i].trim()) && !/^[-*]\s/.test(lines[i].trim()) && !/^\d+\.\s/.test(lines[i].trim()) && !/^>/.test(lines[i].trim()) && !/^[-*_]{3,}$/.test(lines[i].trim())) {
      paraLines.push(lines[i].trim());
      i++;
    }
    if (paraLines.length > 0) {
      elements.push(<p key={`p-${i}`}>{parseInline(paraLines.join(' '))}</p>);
    }
  }

  return elements;
}

function ReportPage() {
  const { reportType } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);

  const reportInfo = REPORT_TYPES[reportType];

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const isPostCheckout = searchParams.get('checkout') === 'success';
    const sessionId = searchParams.get('session_id');

    // webhook 待ちのリトライだけだと、配送に失敗したまま権限が付かない場合に
    // 復旧できない。Stripe に直接照会して確定させてから読み込む。
    async function reconcileThenLoad() {
      if (isPostCheckout && sessionId) {
        try {
          await billingService.reconcileCheckout(sessionId);
        } catch {
          // 失敗しても webhook 側で処理される可能性があるので読み込みは続行
        }
      }
      loadReport();
    }

    async function loadReport(retries = 0) {
      try {
        const data = await billingService.getReport(reportType);
        setReport(data);
        if (isPostCheckout) setShowPurchaseSuccess(true);
      } catch (err) {
        if (err.status === 403 && isPostCheckout && retries < 3) {
          // Webhook may not have processed yet, retry after delay
          setTimeout(() => loadReport(retries + 1), 2000);
          return;
        }
        if (err.status === 403) {
          setAccessDenied(true);
        } else {
          setError(err.message || 'Failed to load report');
        }
      } finally {
        setLoading(false);
      }
    }
    reconcileThenLoad();
  }, [isAuthenticated, reportType, searchParams]);

  const handleRegenerate = useCallback(async () => {
    if (regenerating) return;
    setRegenerating(true);
    setError(null);
    try {
      const data = await billingService.regenerateReport(reportType);
      setReport(data);
    } catch (err) {
      setError(err.message || 'Failed to regenerate report');
    } finally {
      setRegenerating(false);
    }
  }, [reportType, regenerating]);

  if (!isAuthenticated) {
    return (
      <Container>
        <p style={{ textAlign: 'center', color: '#8888a8', padding: '48px 0' }}>
          {t('ログインしてください', 'Please log in')}
        </p>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Spinner />
        <LoadingText>{t('レポートを生成中...', 'Generating report...')}</LoadingText>
      </Container>
    );
  }

  if (accessDenied) {
    return (
      <Container>
        <Header>
          <BackButton onClick={() => navigate(-1)}>&larr;</BackButton>
          <TitleGroup>
            <PageTitle>{t('このレポートにアクセスできません', 'Report Access Required')}</PageTitle>
          </TitleGroup>
        </Header>
        <p style={{ textAlign: 'center', color: '#8888a8', marginBottom: 24, fontSize: '0.88rem' }}>
          {t(
            'このレポートを閲覧するには、個別購入（¥300）またはProプランへの加入が必要です。',
            'Purchase this report (¥300) or subscribe to Pro to access.'
          )}
        </p>
        <ProUpgradeBanner context="general" />
      </Container>
    );
  }

  if (error && !report) {
    return (
      <Container>
        <ErrorText>{error}</ErrorText>
        <FooterButtons>
          <FooterLink onClick={() => navigate(-1)}>{t('戻る', 'Go back')}</FooterLink>
        </FooterButtons>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(-1)}>&larr;</BackButton>
        <TitleGroup>
          <PageTitle>
            {reportInfo ? t(reportInfo.ja, reportInfo.en) : (report?.label || reportType)}
          </PageTitle>
          <PageSub>STRUCT CODE Category Report</PageSub>
        </TitleGroup>
      </Header>

      {showPurchaseSuccess && (
        <ReportSuccessBanner>
          {t('レポートの購入が完了しました！', 'Report purchase complete!')}
        </ReportSuccessBanner>
      )}

      {regenerating && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Spinner />
          <LoadingText>{t('レポートを再生成中...', 'Regenerating report...')}</LoadingText>
        </div>
      )}

      {error && <ErrorText style={{ padding: '12px 0' }}>{error}</ErrorText>}

      <ContentBox style={{ opacity: regenerating ? 0.4 : 1, transition: 'opacity 0.3s' }}>
        <MarkdownBody>
          {renderMarkdown(report?.content)}
        </MarkdownBody>
      </ContentBox>

      <FooterButtons>
        <RegenerateBtn onClick={handleRegenerate} disabled={regenerating}>
          {regenerating ? <SmallSpinner /> : null}
          {regenerating
            ? t('再生成中...', 'Regenerating...')
            : t('レポートを再生成', 'Regenerate Report')
          }
        </RegenerateBtn>
        <FooterLink onClick={() => navigate(-1)}>
          {t('結果ページに戻る', 'Back to Results')}
        </FooterLink>
      </FooterButtons>
    </Container>
  );
}

export default ReportPage;
